import { Component, OnInit } from '@angular/core';
import { Store, select } from '@ngrx/store';
import * as fromRanklistAction from 'src/app/@core/ngrx/ranklist/ranklist.actions';
import { Observable, Subscription, of } from 'rxjs';
import { RankListResponseWrapper } from '../../api/models';
import { RanklistState } from '../../@core/ngrx/ranklist/ranklist.state';
import {
  selectAllRanklistItemsPresent,
  selectChampionsByFilter,
} from '../../@core/ngrx/ranklist/ranklist.selectors';
import { MONTHS } from '../../@core/constants/month-constants';
import { StringMap } from '../../@theme/datatable/datatable.component';
import { isLoading } from 'src/app/@core/ngrx/runningaction/running-action.selectors';
import { ActivatedRoute } from '@angular/router';
import { ThemeService } from 'src/app/@core/services/theme.service';
import { BRANCHES } from 'src/app/@core/constants/branch-constants';

@Component({
  selector: 'app-responsiveranklist-page',
  templateUrl: './responsiveranklist-page.component.html',
  styleUrls: ['./responsiveranklist-page.component.scss'],
})
export class ResponsiveranklistPageComponent implements OnInit {
  public readonly branches = BRANCHES;
  public namespace?: string;
  subForRoomId?: Subscription;
  subForParams?: Subscription;

  date: Date = new Date();
  month: string = new Date().toISOString().substring(0, 10);
  startOfMonth: string = this.month.substring(0, 8) + '01';
  endOfMonth: string = new Date(
    this.date.getFullYear(),
    this.date.getMonth() + 1,
    0,
  )
    .toISOString()
    .substring(0, 10);
  loading$: Observable<boolean> = of(false);

  roomsMap: { [key: string]: string } = {};
  roomId?: string;
  branch?: string[];
  level: string = 'MEDIUM';
  ranklist$: Observable<RankListResponseWrapper[]> = new Observable();
  filters?: StringMap;
  rooms: StringMap = {};
  theme: string | null = null;
  namespaces: StringMap = {};

  public readonly months = MONTHS;

  constructor(
    private store: Store<RanklistState>,
    private activatedRoute: ActivatedRoute,
    private themeService: ThemeService,
  ) {}

  ngOnInit() {
    this.loading$ = this.store.pipe(
      select(isLoading([fromRanklistAction.loadMany])),
    );

    this.ranklist$ = this.store.pipe(select(selectAllRanklistItemsPresent));

    this.subForParams = this.activatedRoute.queryParams.subscribe((params) => {
      let namespaces = params['namespaces'];
      let branches = params['branches'];

      if (namespaces) {
        namespaces = namespaces.split(',');
      }

      if (branches) {
        branches = branches.split(',');
      }

      if (params['color']) {
        this.theme = params['color'];
      } else {
        this.theme = 'dark';
      }
      this.themeService.setTheme(this.theme!);

      if (namespaces && branches) {
        for (let i = 0; i < namespaces.length; ++i) {
          this.namespaces[namespaces[i]] = branches[i];
        }
        this.namespace =
          namespaces && namespaces.length > 0
            ? namespaces[0]
            : 'escapemasters.co.nz';
      } else {
        this.namespace = 'escapemasters.co.nz';
      }

      if (this.theme === 'dark') {
        document.body.classList.add('dark-theme');
      } else {
        document.body.classList.add('light-theme');
      }
    });

    this.subForRoomId = this.ranklist$.subscribe(
      (ranklist) => (this.rooms = this.getRooms(ranklist)),
    );
  }

  private dispatchLoadRanklist(roomId: string): void {
    this.store.dispatch(
      fromRanklistAction.loadMany({
        month: this.month,
        roomId: roomId ? roomId : 'not_defined',
        level: this.level,
        nameSpace: this.namespace!,
      }),
    );
  }

  ngOnDestroy(): void {
    this.subForRoomId?.unsubscribe();
    this.subForParams?.unsubscribe();
  }

  private getRooms(ranklist: RankListResponseWrapper[]): StringMap {
    const mergedRooms: StringMap = {};
    const roomNames = new Set<string>();

    const rooms: { roomId: string; roomName: string }[] = [];

    ranklist.forEach((entry) => {
      entry.rooms!.forEach((room) => {
        if (room.roomId && room.name) {
          rooms.push({
            roomId: room.roomId,
            roomName: room.name.toUpperCase(),
          });
        }
      });
    });

    rooms.sort((a, b) => (a.roomName > b.roomName ? 1 : -1));

    rooms.forEach(({ roomId, roomName }) => {
      const baseRoomName = roomName.replace(/ \d+$/, '');

      if (!roomNames.has(baseRoomName)) {
        roomNames.add(baseRoomName);
        mergedRooms[roomId] = baseRoomName;
      }
    });

    const firstRoomId = Object.keys(mergedRooms)[0];
    this.roomId = firstRoomId;
    this.dispatchLoadRanklist(this.roomId);

    if (firstRoomId) {
      this.subForRoomId?.unsubscribe();
    }

    return mergedRooms;
  }

  public onFilterChange(event: StringMap): void {
    this.filters = event;

    if (event['month']) {
      const selectedYear = parseInt(event['month'].split('.')[0]);
      const selectedMonth = parseInt(event['month'].split('.')[1]);
      this.month =
        event['month'].split('.')[0] +
        '-' +
        event['month'].split('.')[1] +
        '-' +
        new Date(selectedYear, selectedMonth, 0).getDate();
      this.startOfMonth = new Date(selectedYear, selectedMonth - 1, 1)
        .toISOString()
        .split('T')[0];
      this.endOfMonth = new Date(selectedYear, selectedMonth, 0)
        .toISOString()
        .split('T')[0];
    }

    if (event['room']) {
      this.roomId = event['room'];
    }

    if (event['level']) {
      this.level = event['level'];
    }

    if (this.roomId) {
      this.dispatchLoadRanklist(this.roomId);
    }

    this.ranklist$ = this.store.pipe(select(selectAllRanklistItemsPresent));
  }
}
